/**
 * External-app admin panel — bearer token + bearer-path allowlist.
 *
 * Shown on the app page for an `kind: 'external'` app to its managers.
 * External apps authenticate purely via `kin_apt_` bearer tokens, so this
 * panel is the surface for:
 *
 *   - Minting a one-time bearer token (POST /api/apps/:slug/tokens) and
 *     revealing the plaintext exactly once — same UX as the managed-app
 *     token sections (copy affordance + "shown once" warning).
 *   - Editing the bearer-path allowlist (PUT /api/apps/:slug/bearer-paths)
 *     — the set of request paths a bearer token is allowed to reach.
 *
 * Manager-gated by the caller (only rendered when `app.is_manager`); the
 * control plane enforces it independently.
 */

import { useState } from 'react';
import { mintAppToken, setBearerPaths } from '../api/client.js';
import type { AppSummary, MintTokenResponse } from '../api/types.js';
import { IconCopy, IconShield, IconX } from './Icons.js';
import { Banner, Button, Card, CardHeader, Field, Mono, inputClass } from './ui.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

const EXPIRY_OPTIONS: { days: number; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days (recommended)' },
  { days: 180, label: '180 days' },
  { days: 365, label: '1 year (max)' },
];

/** Split a textarea value into a deduped, trimmed list of paths. */
function parsePaths(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\n,]+/)) {
    const p = part.trim();
    if (p && !seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

export interface ExternalAppSectionProps {
  app: Pick<AppSummary, 'slug' | 'name' | 'external_hostnames' | 'bearer_paths'>;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  setActionMessage: (msg: ActionMessage | null) => void;
  reload: () => Promise<void>;
}

export function ExternalAppSection({
  app,
  busy,
  setBusy,
  setActionMessage,
  reload,
}: ExternalAppSectionProps) {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [revealed, setRevealed] = useState<MintTokenResponse | null>(null);
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [pathsDraft, setPathsDraft] = useState(() => (app.bearer_paths ?? []).join('\n'));

  const hostnames = app.external_hostnames ?? [];

  async function mint(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    await runAction({
      do: () => mintAppToken(app.slug, { label: label.trim(), expiresInDays }),
      onSuccess: (res) => {
        setRevealed(res);
        setLabel('');
        return `Token created. Copy it now — you won't see it again.`;
      },
      errorPrefix: "Couldn't create token",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function savePaths(e: React.FormEvent) {
    e.preventDefault();
    const paths = parsePaths(pathsDraft);
    await runAction({
      do: () => setBearerPaths(app.slug, paths),
      onSuccess: (res) => {
        setPathsDraft(res.bearer_paths.join('\n'));
        return 'Bearer paths saved.';
      },
      errorPrefix: "Couldn't save bearer paths",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  function copyToken() {
    if (!revealed) return;
    void navigator.clipboard.writeText(revealed.token).then(() => {
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 1500);
    });
  }

  return (
    <div className="space-y-5">
      <Banner tone="info">
        <span className="inline-flex items-center gap-1.5">
          <IconShield size={14} />
          Plaintext tokens are shown exactly once. Save them at creation; the server never returns
          them again.
        </span>
      </Banner>

      {revealed && (
        <Card>
          <CardHeader
            title="New token — copy it now"
            description="This is the only time you'll see the plaintext. After you dismiss this, only metadata is shown."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRevealed(null)}
                aria-label="Dismiss"
              >
                <IconX size={13} />
                Done
              </Button>
            }
          />
          <div className="px-5 py-4">
            <div className="flex items-center gap-2">
              <Mono className="flex-1 break-all px-3 py-2 rounded-md bg-surface-2 border border-line text-xs">
                {revealed.token}
              </Mono>
              <Button variant="primary" size="sm" onClick={copyToken}>
                <IconCopy size={13} />
                {copyConfirm ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Mint a bearer token"
          description={
            hostnames.length > 0 ? (
              <>
                Issues a bearer token for callers reaching <Mono>{hostnames[0]}</Mono>
                {hostnames.length > 1 ? ` (+${hostnames.length - 1} more)` : ''}.
              </>
            ) : (
              'Issues a bearer token for programmatic callers to this app.'
            )
          }
        />
        <form
          onSubmit={(e) => void mint(e)}
          className="px-5 py-4 grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end"
        >
          <Field label="Label" required htmlFor="ext-token-label">
            <input
              id="ext-token-label"
              type="text"
              required
              maxLength={64}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ci-runner"
              className={inputClass}
            />
          </Field>
          <Field label="Expires" htmlFor="ext-token-expiry">
            <select
              id="ext-token-expiry"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className={inputClass}
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            disabled={busy || !label.trim()}
          >
            Generate
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Bearer paths"
          description="Request paths a bearer token is allowed to reach on this app. One per line (or comma-separated)."
        />
        <form onSubmit={(e) => void savePaths(e)} className="px-5 py-4 space-y-3">
          <Field label="Bearer paths" htmlFor="ext-bearer-paths">
            <textarea
              id="ext-bearer-paths"
              value={pathsDraft}
              onChange={(e) => setPathsDraft(e.target.value)}
              rows={4}
              className={`${inputClass} font-mono`}
              placeholder="/api&#10;/mcp"
            />
          </Field>
          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="sm" loading={busy} disabled={busy}>
              Save paths
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
